import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { URL_MAP } from '../../shared/urlMap';
import { environment as ENV } from '../../../environments/environment';
import {
  TipoPrimaDTO,
  TiposPrimaRequest,
} from '../../models/tipos-prima/tipo-prima.model';

@Injectable({
  providedIn: 'root',
})
export class TiposPrimaService {
  private apiUrl = `${ENV.HOST}${URL_MAP.API.TIPOS_PRIMA}`;
  /** Resultados ya resueltos por `grupoPrograma + lineaNegocio`. */
  private readonly resultCache = new Map<string, TipoPrimaDTO[]>();
  /** Peticiones HTTP en curso (deduplicación concurrente). */
  private readonly inflightRequests = new Map<string, Observable<TipoPrimaDTO[]>>();

  constructor(private http: HttpClient) {}

  consultarTiposPrima(
    request: TiposPrimaRequest,
    opciones?: { forceRefresh?: boolean },
  ): Observable<TipoPrimaDTO[]> {
    const grupoPrograma = String(request.grupoPrograma ?? '').trim();
    const lineaNegocio = String(request.lineaNegocio ?? '').trim();
    if (!grupoPrograma || !lineaNegocio) {
      return of([]);
    }

    const cacheKey = TiposPrimaService.buildCacheKey(
      grupoPrograma,
      lineaNegocio,
    );
    if (opciones?.forceRefresh) {
      this.invalidarCache(grupoPrograma, lineaNegocio);
    }
    const cached = this.resultCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const inflight = this.inflightRequests.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const body: TiposPrimaRequest = { grupoPrograma, lineaNegocio };

    const request$ = this.http.post<unknown>(this.apiUrl, body).pipe(
      map((response) =>
        TiposPrimaService.extractItemsArray(response).map((item) =>
          TiposPrimaService.mapItemToDto(item),
        ),
      ),
      tap({
        next: (results) => {
          this.resultCache.set(cacheKey, results);
          this.inflightRequests.delete(cacheKey);
        },
        error: () => {
          this.inflightRequests.delete(cacheKey);
        },
      }),
      shareReplay(1),
    );

    this.inflightRequests.set(cacheKey, request$);
    return request$;
  }

  /** Permite reconsultar POST `/tipos-prima` tras deseleccionar y volver a elegir subtipo. */
  invalidarCache(grupoPrograma?: string, lineaNegocio?: string): void {
    const gp = String(grupoPrograma ?? '').trim();
    const ln = String(lineaNegocio ?? '').trim();
    if (gp && ln) {
      const cacheKey = TiposPrimaService.buildCacheKey(gp, ln);
      this.resultCache.delete(cacheKey);
      this.inflightRequests.delete(cacheKey);
      return;
    }
    this.resultCache.clear();
    this.inflightRequests.clear();
  }

  private static buildCacheKey(
    grupoPrograma: string,
    lineaNegocio: string,
  ): string {
    return `${grupoPrograma}\0${lineaNegocio}`;
  }

  private static extractItemsArray(raw: unknown): any[] {
    if (raw == null) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      for (const key of ['data', 'Data', 'result', 'content', 'items']) {
        const inner = o[key];
        if (Array.isArray(inner)) {
          return inner;
        }
      }
    }
    return [];
  }

  private static mapItemToDto(item: any): TipoPrimaDTO {
    return {
      idTipoPrima: Number(item?.idTipoPrima ?? item?.IdTipoPrima ?? 0),
      tipoPrima: String(item?.tipoPrima ?? item?.TipoPrima ?? '').trim(),
      descripcion: String(item?.descripcion ?? item?.Descripcion ?? '').trim(),
    };
  }
}
